/**
 * Mode metadata for the PO Line Dashboard drill-down modal.
 *
 * Every tile / donut slice / bar segment opens the modal with one of these
 * modes; the backend (``analytics/po_line_dashboard/drilldown.py``) builds the
 * rows from the same predicates the tile used, so the count always matches.
 */
import type { POLineDrilldownEntity, POLineDrilldownMode } from '../../../lib/api/po-line-dashboard';
import { CUSTOMER_REQUIRED_DATE, ORIGINAL_REQUIRED_DATE_SHORT, SUPPLIER_PROMISE_DATE_SHORT } from '../../../config/dateLabels';

export interface POLineDrilldownFilters {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
}

export interface ModeMeta {
  title: string;
  description: string;
  entity: POLineDrilldownEntity;
  defaultOrdering: string;
}

export const MODE_META: Record<POLineDrilldownMode, ModeMeta> = {
  due:          { title: 'PO lines due',               description: 'Every non-cancelled PO line due in the period', entity: 'line', defaultOrdering: 'delivery_date' },
  open:         { title: 'Open PO lines',              description: 'Lines due in the period that still owe quantity', entity: 'line', defaultOrdering: 'delivery_date' },
  received:     { title: 'Received PO lines',          description: 'Lines due in the period with at least one receipt', entity: 'line', defaultOrdering: 'delivery_date' },
  arrears:      { title: 'Arrears — past-due open PO lines', description: 'Open lines past their ODD (beyond the arrears buffer)', entity: 'line', defaultOrdering: 'odd_date' },
  otif_passed:  { title: 'OTIF passed — delivered in full within tolerance', description: 'Lines flagged OTIF', entity: 'line', defaultOrdering: 'odd_date' },
  otif_failed:  { title: 'OTIF failed — delivered late, early or short', description: 'Closed lines that scored zero', entity: 'line', defaultOrdering: 'odd_date' },
  otif_arrears: { title: 'OTIF arrears — past due, not delivered', description: 'Open lines past their due date. Never scored — counted in the denominator, not judged as failures', entity: 'line', defaultOrdering: 'odd_date' },
  otif_pending: { title: 'OTIF pending — not yet due', description: 'Open lines whose ODD has not passed', entity: 'line', defaultOrdering: 'odd_date' },
  otif_due:     { title: 'OTIF — resolved PO lines',   description: 'Passed + failed + arrears (the OTIF denominator)', entity: 'line', defaultOrdering: 'odd_date' },
  // CR-7312: OTD is the receipt measure (M4), as on the Default dashboard:
  // receipts in the period by receipt date, so On time + Not on time = All.
  otd_due:      { title: 'OTD — receipts in the period', description: 'Every goods receipt counted for OTD, by receipt date', entity: 'grn', defaultOrdering: '-receipt_date' },
  otd_met:      { title: 'OTD met — receipts on time', description: 'Receipts inside the early/late tolerance window (overrides count as on time)', entity: 'grn', defaultOrdering: '-receipt_date' },
  otd_missed:   { title: 'OTD missed — receipts not on time', description: 'Receipts too early, too late or not yet judged', entity: 'grn', defaultOrdering: '-receipt_date' },
  grn_all:      { title: 'Deliveries',                 description: 'GRNs received in the period', entity: 'grn', defaultOrdering: '-receipt_date' },
  grn_on_time:  { title: 'On-time deliveries',         description: 'GRNs received inside the OTIF tolerance window', entity: 'grn', defaultOrdering: '-receipt_date' },
  grn_early:    { title: 'Early deliveries',           description: 'GRNs received before the tolerance window', entity: 'grn', defaultOrdering: '-receipt_date' },
  grn_late:     { title: 'Late deliveries',            description: 'GRNs received after the tolerance window', entity: 'grn', defaultOrdering: '-receipt_date' },
  ncrs:         { title: 'Quality — NCRs',             description: 'NCRs raised in the period', entity: 'ncr', defaultOrdering: '-ncr_date' },
  ppm:          { title: 'Quality — rejects',          description: 'Supplier-liability NCRs with rejected quantity in the period', entity: 'ncr', defaultOrdering: '-ncr_date' },
  appeals:      { title: 'Delivery — Appeals',         description: 'Delivery appeals raised in the period', entity: 'appeal', defaultOrdering: '-created_at' },
};

/**
 * Sibling tab groups — clicking any segment of a chart or ring opens the modal
 * with tabs for the WHOLE group so the user can step through every category
 * without re-opening (mirrors the Scheduled Dashboard's CurrentWeekDrilldownModal).
 * Single-metric tiles (due / open / received / arrears / ncrs / ppm / appeals)
 * are in no group and open without tabs.
 */
export interface DrilldownTab {
  mode: POLineDrilldownMode;
  label: string;
}

/** Deliveries by Day / by Week stacked bars (GRN verdicts + lines still owed). */
const BAR_TABS: DrilldownTab[] = [
  { mode: 'grn_on_time', label: 'On time' },
  { mode: 'grn_early',   label: 'Early' },
  { mode: 'grn_late',    label: 'Late' },
  { mode: 'open',        label: 'Pending' },
];
/** OTD ring — GRN verdicts in the to-date window. */
const OTD_MIX_TABS: DrilldownTab[] = BAR_TABS.filter((t) => t.mode !== 'open');
/** OTIF ring + OTIF KPI tile: All (denominator) / Passed / Pending / Arrears /
 *  Failed. CR-6327 split Arrears out of Failed — a past-due undelivered line
 *  is not a supplier failure, and the canonical definition says so. */
const OTIF_TABS: DrilldownTab[] = [
  { mode: 'otif_due',     label: 'All' },
  { mode: 'otif_passed',  label: 'Passed' },
  { mode: 'otif_pending', label: 'Pending' },
  { mode: 'otif_arrears', label: 'Arrears' },
  { mode: 'otif_failed',  label: 'Failed' },
];

/** OTD KPI tile: All (every receipt counted) / On time / Not on time — the
 *  tile opens on exactly the receipts its "met/due" caption divides by, and
 *  the two splits add up (CR-7312). */
const OTD_TABS: DrilldownTab[] = [
  { mode: 'otd_due',    label: 'All' },
  { mode: 'otd_met',    label: 'On time' },
  { mode: 'otd_missed', label: 'Not on time' },
];

/** Where the click came from — decides which sibling group the modal shows. */
export type DrilldownTabContext = 'bars' | 'otd_mix';

/** The sibling tab group for a click, or null for single-metric tiles (due /
 *  open / received / arrears / ncrs / ppm / appeals). The GRN modes need a
 *  context because "open" is also the Cost tile's own mode; the OTIF and OTD
 *  line modes are unique to their groups. */
export function getTabGroup(mode: POLineDrilldownMode, context?: DrilldownTabContext): DrilldownTab[] | null {
  if (context === 'bars') return BAR_TABS;
  if (context === 'otd_mix') return OTD_MIX_TABS;
  if (OTD_TABS.some((t) => t.mode === mode)) return OTD_TABS;
  return OTIF_TABS.some((t) => t.mode === mode) ? OTIF_TABS : null;
}

export interface ColumnDef {
  id: string;
  label: string;
  align?: 'left' | 'right';
  /** Backend ordering key; null = not sortable. */
  sort: string | null;
  /** Header tooltip explaining what the column shows / drives. */
  hint?: string;
}

export const LINE_COLUMNS: ColumnDef[] = [
  { id: 'odd_date', label: ORIGINAL_REQUIRED_DATE_SHORT, sort: 'odd_date', hint: 'Original due date — the date the OTIF verdict and arrears are judged against' },
  { id: 'delivery_date', label: CUSTOMER_REQUIRED_DATE, sort: 'delivery_date', hint: 'The date the customer currently requires, as the ERP last re-planned it' },
  { id: 'last_receipt_date', label: 'Last Receipt', sort: 'last_receipt_date', hint: 'Latest GRN receipt date — the line fails OTIF when this is outside the tolerance window even if fully received' },
  { id: 'supplier_promise_date', label: SUPPLIER_PROMISE_DATE_SHORT, sort: 'supplier_promise_date', hint: 'Supplier-committed delivery date for this line' },
  { id: 'po_number', label: 'PO #', sort: 'po_number' },
  { id: 'line_number', label: 'Line', align: 'right', sort: 'line_number' },
  { id: 'part_number', label: 'Part', sort: 'part_number' },
  { id: 'part_description', label: 'Part Description', sort: null },
  { id: 'supplier_name', label: 'Supplier', sort: 'supplier_name' },
  { id: 'quantity', label: 'Qty', align: 'right', sort: 'quantity', hint: 'Quantity ordered on the line' },
  { id: 'received_qty', label: 'Received Qty', align: 'right', sort: 'received_qty', hint: 'Quantity received against this line (sum of GRNs)' },
  { id: 'outstanding_qty', label: 'Open Qty', align: 'right', sort: 'outstanding_qty', hint: 'Outstanding qty = Qty − Received Qty' },
  { id: 'line_value', label: 'Value', align: 'right', sort: 'line_value', hint: 'Qty × unit price in the reporting currency' },
  { id: 'status', label: 'Status', sort: 'status' },
  { id: 'otif_status', label: 'OTIF Status', sort: 'otif_status', hint: 'Per-line OTIF verdict (OTIF / Early / Late / Short / Arrear / Pending) — overrides show as Passed (Override)' },
];

export const GRN_COLUMNS: ColumnDef[] = [
  { id: 'receipt_date', label: 'Receipt Date', sort: 'receipt_date', hint: 'Date the goods were received (GRN) — compared against the due date to set the verdict' },
  { id: 'odd_date', label: ORIGINAL_REQUIRED_DATE_SHORT, sort: 'odd_date', hint: 'Original due date — receipt date vs. this drives the on-time verdict' },
  { id: 'grn_number', label: 'GRN #', sort: 'grn_number' },
  { id: 'po_number', label: 'PO #', sort: 'po_number' },
  { id: 'line_number', label: 'Line', align: 'right', sort: 'line_number' },
  { id: 'part_number', label: 'Part', sort: 'part_number' },
  { id: 'part_description', label: 'Part Description', sort: null },
  { id: 'supplier_name', label: 'Supplier', sort: 'supplier_name' },
  { id: 'ordered_qty', label: 'Qty Ordered', align: 'right', sort: 'ordered_qty', hint: 'Quantity ordered on the PO line this GRN was received against' },
  { id: 'quantity_received', label: 'Qty Received', align: 'right', sort: 'quantity_received', hint: 'Quantity received on this GRN' },
  { id: 'on_time_status', label: 'On-time Status', sort: 'on_time_status', hint: 'Receipt vs due date verdict (Early / On time / Late)' },
];

export const NCR_COLUMNS: ColumnDef[] = [
  { id: 'ncr_date', label: 'NCR Date', sort: 'ncr_date' },
  { id: 'ncr_number', label: 'NCR #', sort: 'ncr_number' },
  { id: 'part_number', label: 'Part', sort: 'part_number' },
  { id: 'part_description', label: 'Part Description', sort: null },
  { id: 'supplier_name', label: 'Supplier', sort: 'supplier_name' },
  { id: 'reject_qty', label: 'Reject Qty', align: 'right', sort: 'reject_qty' },
  { id: 'status', label: 'Status', sort: 'status' },
];

export const APPEAL_COLUMNS: ColumnDef[] = [
  { id: 'created_at', label: 'Raised', sort: 'created_at' },
  { id: 'grn_number', label: 'GRN', sort: null },
  { id: 'po_number', label: 'PO #', sort: 'po_number' },
  { id: 'line_number', label: 'Line', align: 'right', sort: null },
  { id: 'part_number', label: 'Part', sort: null },
  { id: 'supplier_name', label: 'Supplier', sort: 'supplier_name' },
  { id: 'qty_claimed', label: 'Qty Claimed', align: 'right', sort: 'qty_claimed' },
  { id: 'status', label: 'Status', sort: 'status' },
];

export const COLUMNS_BY_ENTITY: Record<POLineDrilldownEntity, ColumnDef[]> = {
  line: LINE_COLUMNS,
  grn: GRN_COLUMNS,
  ncr: NCR_COLUMNS,
  appeal: APPEAL_COLUMNS,
};

/** Map a weekday label (Mon..Sun) to the ISO date inside the week starting
 *  on ``weekStartIso`` (a Monday, as the backend window_start is). */
export function dayIsoFor(weekStartIso: string, day: string): string {
  const offsets: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  const d = new Date(weekStartIso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + (offsets[day] ?? 0));
  return d.toISOString().slice(0, 10);
}
